// client/src/routes/app/tenants/$tenantId/route.tsx — SCR-TEN-002, the tabs layout · TEN-004
// (04 §8.4, §5.10).
//
// THE TAB SET COMES FROM DOC 05, NOT DOC 04, and the two disagree. 04 §8.4's note lists
// „Profil · Contracte · Istoric plăți (M2)"; 05 TEN-004's own Refs line lists „profil PF/PJ,
// contacte, istoric". Doc 05 is the item's scope sentence, it is the one that has room for the
// contacts editor TEN-001 explicitly owns („CRUD /api/v1/tenants: PF/PJ profiles, CONTACTS,
// …"), and it folds contracts and payments into one „istoric" — which is also 05 TEN-004's
// body text: „history tab renders contracts and (from M2) payments". So: Profil · Contacte ·
// Istoric. See DEVIATIONS D-377.
//
// D-2860 widens it to everything the application holds about the tenant: Profil (with „Pe
// scurt" at its head) · Locuințe (every property and contract, current and past) · Plăți ·
// Reparații · Documente · Istoric (one chronological list of all of it) · Contacte. The order is
// the order a landlord asks the questions in: who they are, where they lived, what they paid,
// what broke, what paper there is, and then the whole story.
//
// Route-linked `<Link>`s (§5.10), so the active tab survives refresh and share, and TanStack
// puts `aria-current="page"` on it. The header carries the tenant's identity and the two
// destructive-ish actions, so every tab has them without repeating them three times.
//
// The head is the shared `DetailHeader` since the console's third visual pass (D-1842): the way
// back to the list, a person glyph, the e-mail as the identity line and the chips under it. The
// delete confirm is the shared `ConfirmDialog`; the refusal a tenant on an ACTIVE contract earns
// still renders inside it, as before.
//
// „Contract nou" leads the actions (D-2880): the contract wizard opens with this tenant already
// its titular, so a lease is drawn up from the person's side as readily as from the flat's.

import { Link, Outlet, createFileRoute, linkOptions, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { DetailHeader } from '@/components/layout/DetailHeader';
import { ProfileTabs } from '@/components/layout/ProfileTabs';
import { RequirePermission } from '@/components/layout/route-guards';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { CuiBadge } from '@/features/tenants/CuiBadge';
import { readStoredCuiState } from '@/features/tenants/cui';
import { useTenantErrorMessage } from '@/features/tenants/errors';
import { useDeleteTenant, useTenant } from '@/features/tenants/queries';
import { wireNumber } from '@/lib/wire';
import { useCan } from '@/session';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/tenants/$tenantId')({
  component: TenantDetailLayout,
});

function TenantDetailLayout() {
  const { tenantId } = Route.useParams();
  const { t } = useTranslation();
  const tenant = useTenant(tenantId);
  // The history tabs are each a module's rows (D-2860). A tab whose module the reader may not
  // read is not offered, per 04 §8.3; opened by URL it says why it is empty.
  const canReadPayments = useCan('Payments.Read');
  const canReadRepairs = useCan('Operations.Read');
  const canReadDocuments = useCan('Documents.Read');

  if (tenant.isPending) return <LoadingState rows={4} />;
  if (tenant.isError) {
    return <ErrorState error={tenant.error} onRetry={() => void tenant.refetch()} />;
  }

  return (
    <RequirePermission permission="Leasing.Read">
      <DetailHeader
        backTo="/app/tenants"
        backLabel={t('tenants.list.title')}
        glyph={tenant.data.taxpayerType === 'PJ' ? 'building' : 'user-circle'}
        title={tenant.data.displayName}
        subtitle={tenant.data.email ?? undefined}
        chips={
          <>
            <span className="chip chip-neutral">
              {t(`enums.TaxpayerType.${tenant.data.taxpayerType}`)}
            </span>
            {tenant.data.taxpayerType === 'PJ' ? (
              <CuiBadge
                state={readStoredCuiState(tenant.data.cui, tenant.data.cuiValidationResult)}
              />
            ) : null}
            {wireNumber(tenant.data.activeContractCount) > 0 ? (
              <span className="chip chip-success">
                {t('tenants.detail.activeContracts', {
                  count: wireNumber(tenant.data.activeContractCount),
                })}
              </span>
            ) : null}
          </>
        }
        actions={<HeaderActions tenantId={tenantId} tenantName={tenant.data.displayName} />}
      />

      {/* D-2900: the shared section bar, in three groups — who the tenant is; where they lived
          and what that left behind (money, repairs, paper); and the whole story with the people
          to call. The order is D-2860's and does not change. */}
      <ProfileTabs
        label={t('tenants.detail.tabsLabel')}
        groups={[
          [
            {
              label: t('tenants.detail.tabs.profile'),
              icon: 'user-circle',
              primary: true,
              link: linkOptions({ to: '/app/tenants/$tenantId', params: { tenantId } }),
              exact: true,
            },
          ],
          [
            {
              label: t('tenants.detail.tabs.homes'),
              icon: 'home',
              primary: true,
              link: linkOptions({ to: '/app/tenants/$tenantId/homes', params: { tenantId } }),
            },
            canReadPayments && {
              label: t('tenants.detail.tabs.payments'),
              icon: 'banknotes',
              primary: true,
              link: linkOptions({ to: '/app/tenants/$tenantId/payments', params: { tenantId } }),
            },
            canReadRepairs && {
              label: t('tenants.detail.tabs.repairs'),
              icon: 'exclamation-triangle',
              primary: true,
              link: linkOptions({ to: '/app/tenants/$tenantId/repairs', params: { tenantId } }),
            },
            canReadDocuments && {
              label: t('tenants.detail.tabs.documents'),
              icon: 'folder',
              primary: true,
              link: linkOptions({ to: '/app/tenants/$tenantId/documents', params: { tenantId } }),
            },
          ],
          [
            {
              label: t('tenants.detail.tabs.history'),
              icon: 'clock',
              link: linkOptions({ to: '/app/tenants/$tenantId/history', params: { tenantId } }),
            },
            {
              label: t('tenants.detail.tabs.contacts'),
              icon: 'envelope',
              link: linkOptions({ to: '/app/tenants/$tenantId/contacts', params: { tenantId } }),
            },
          ],
        ]}
      />

      <Outlet />
    </RequirePermission>
  );
}

function HeaderActions({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canWrite = useCan('Leasing.Write');
  const remove = useDeleteTenant();
  const describe = useTenantErrorMessage();
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  if (!canWrite) return null;

  const destroy = async () => {
    setFailure(null);
    try {
      await remove.mutateAsync({ path: { id: tenantId } });
      setConfirming(false);
      toast.success(t('tenants.detail.deleted'));
      await navigate({ to: '/app/tenants' });
    } catch (error) {
      // A tenant on an ACTIVE contract is refused with a business-rule 422, the way a property
      // is (05 PROP-001 criterion 2, mirrored). That refusal belongs in the dialog that asked.
      setFailure(describe(error));
    }
  };

  return (
    <>
      <Link
        to="/app/contracts/new"
        search={{ tenantId }}
        className="btn btn-primary btn-sm"
      >
        <Icon name="document-text" className="h-4 w-4" />
        {t('tenants.detail.newContract')}
      </Link>
      <Link
        to="/app/tenants/$tenantId/edit"
        params={{ tenantId }}
        className="btn btn-outline btn-sm"
      >
        <Icon name="cog" className="h-4 w-4" />
        {t('tenants.detail.edit')}
      </Link>
      <button
        type="button"
        className="btn btn-outline btn-error btn-sm"
        onClick={() => setConfirming(true)}
      >
        {t('common.delete')}
      </button>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => void destroy()}
        title={t('tenants.detail.deleteTitle')}
        description={t('tenants.detail.deleteBody', { name: tenantName })}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        pendingLabel={t('common.saving')}
      >
        {failure ? (
          <div className="alert alert-error text-sm" role="alert">
            {failure}
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
